import { useCallback, useEffect, useState } from 'react';
import {
  Page,
  Card,
  FormLayout,
  TextField,
  BlockStack,
  InlineStack,
  Text,
  Button,
  Checkbox,
  Banner,
  RangeSlider,
  ButtonGroup,
} from '@shopify/polaris';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useApi } from '../../providers/ApiProvider';
import {
  DEFAULT_POPUP_DESIGN,
  toColorInputValue,
  type PopupDesign,
} from './popupDefaults';
import { PopupPreview } from './PopupPreview';
import {
  ContextualSaveBar,
  SavingOverlay,
  isShopifyEmbedded,
  showAppToast,
  useUnsavedChanges,
} from '../../components/ContextualSaveBar';
import { ThemeColorRefresh } from '../../components/ThemeColorRefresh';
import { t } from '../../i18n';

function ColorField({
  label,
  value,
  onChange,
  helpText,
  disabled,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  helpText?: string;
  disabled?: boolean;
}) {
  const pickerValue = toColorInputValue(value, '#000000');
  return (
    <div>
      <Text as="p" variant="bodyMd">
        {label}
      </Text>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          marginTop: 6,
        }}
      >
        <input
          type="color"
          value={pickerValue}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
          aria-label={label}
          style={{
            width: 40,
            height: 36,
            padding: 0,
            border: '1px solid #c9cccf',
            borderRadius: 8,
            background: 'transparent',
            cursor: disabled ? 'not-allowed' : 'pointer',
            opacity: disabled ? 0.5 : 1,
          }}
        />
        <div style={{ flex: 1 }}>
          <TextField
            label={label}
            labelHidden
            value={value}
            onChange={onChange}
            autoComplete="off"
            disabled={disabled}
            helpText={helpText}
          />
        </div>
      </div>
    </div>
  );
}

export function PopupEditorPage() {
  const api = useApi();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const returnTo = searchParams.get('return') || '/widgets';

  const [design, setDesign] = useState<PopupDesign>({ ...DEFAULT_POPUP_DESIGN });
  const [widgetId, setWidgetId] = useState<string | null>(null);
  const [previewMode, setPreviewMode] = useState<'desktop' | 'mobile'>('desktop');
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const embedded = isShopifyEmbedded();
  const { dirty, markClean, getBaseline, markCleanAfterDiscard } =
    useUnsavedChanges(design);

  const patch = useCallback(<K extends keyof PopupDesign>(key: K, value: PopupDesign[K]) => {
    setDesign((d) => ({ ...d, [key]: value }));
  }, []);

  useEffect(() => {
    setLoading(true);
    api
      .get<
        Array<{
          id: string;
          type: string;
          content?: Record<string, unknown>;
          styles?: Record<string, unknown>;
        }>
      >('/widgets')
      .then((list) => {
        const popup = (list || []).find((w) => w.type === 'POPUP');
        if (!popup) {
          const defaults = { ...DEFAULT_POPUP_DESIGN };
          setDesign(defaults);
          markClean(defaults);
          return;
        }
        setWidgetId(popup.id);
        const c = popup.content || {};
        const s = popup.styles || {};
        const next: PopupDesign = {
          ...DEFAULT_POPUP_DESIGN,
          title: String(c.title ?? DEFAULT_POPUP_DESIGN.title),
          description: String(c.description ?? DEFAULT_POPUP_DESIGN.description),
          ctaLabel: String(c.ctaLabel ?? DEFAULT_POPUP_DESIGN.ctaLabel),
          secondaryLabel: String(
            c.secondaryLabel ?? DEFAULT_POPUP_DESIGN.secondaryLabel,
          ),
          accentColor: String(s.accentColor ?? DEFAULT_POPUP_DESIGN.accentColor),
          backgroundColor: String(
            s.backgroundColor ?? DEFAULT_POPUP_DESIGN.backgroundColor,
          ),
          textColor: String(s.textColor ?? DEFAULT_POPUP_DESIGN.textColor),
          mutedColor: String(s.mutedColor ?? DEFAULT_POPUP_DESIGN.mutedColor),
          overlayColor: String(
            s.overlayColor ?? DEFAULT_POPUP_DESIGN.overlayColor,
          ),
          borderRadius: Number(
            s.borderRadius ?? DEFAULT_POPUP_DESIGN.borderRadius,
          ),
          maxWidth: Number(s.maxWidth ?? DEFAULT_POPUP_DESIGN.maxWidth),
          showClose: s.showClose !== false,
          giftCardBg: String(s.giftCardBg ?? DEFAULT_POPUP_DESIGN.giftCardBg),
          giftCardBorder: String(
            s.giftCardBorder ?? DEFAULT_POPUP_DESIGN.giftCardBorder,
          ),
          selectedBorder: String(
            s.selectedBorder ?? DEFAULT_POPUP_DESIGN.selectedBorder,
          ),
          buttonTextColor: String(
            s.buttonTextColor ?? DEFAULT_POPUP_DESIGN.buttonTextColor,
          ),
          fontFamily: String(s.fontFamily ?? DEFAULT_POPUP_DESIGN.fontFamily),
          autoFitTheme: s.autoFitTheme !== false,
          themeSchemeId:
            typeof s.themeSchemeId === 'string' ? s.themeSchemeId : null,
        };
        setDesign(next);
        markClean(next);
      })
      .catch(() => {
        const defaults = { ...DEFAULT_POPUP_DESIGN };
        setDesign(defaults);
        markClean(defaults);
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api]);

  async function save() {
    setSaving(true);
    setError(null);
    setMessage(null);
    try {
      const payload = {
        name: 'Free gift pop-up',
        type: 'POPUP' as const,
        placement: 'CART' as const,
        content: {
          title: design.title,
          description: design.description,
          ctaLabel: design.ctaLabel,
          secondaryLabel: design.secondaryLabel,
        },
        styles: {
          accentColor: design.accentColor,
          backgroundColor: design.backgroundColor,
          textColor: design.textColor,
          mutedColor: design.mutedColor,
          overlayColor: design.overlayColor,
          borderRadius: design.borderRadius,
          maxWidth: design.maxWidth,
          showClose: design.showClose,
          giftCardBg: design.giftCardBg,
          giftCardBorder: design.giftCardBorder,
          selectedBorder: design.selectedBorder,
          buttonTextColor: design.buttonTextColor,
          fontFamily: design.fontFamily,
          autoFitTheme: design.autoFitTheme,
          themeSchemeId: design.themeSchemeId,
        },
        layout: { position: 'center', fullWidth: false },
        animations: { enter: 'fade', durationMs: 220 },
        isEnabled: true,
      };

      if (widgetId) {
        await api.patch(`/widgets/${widgetId}`, payload);
      } else {
        const created = await api.post<{ id: string }>('/widgets', payload);
        setWidgetId(created.id);
      }
      await api.patch('/stores/setup', { widgetCustomized: true }).catch(() => undefined);
      markClean(design);
      setMessage(t("Pop-up saved. Campaigns can use this design storefront-wide."));
      showAppToast(t("Pop-up saved"));
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Save failed';
      setError(msg);
      showAppToast(msg, { isError: true });
    } finally {
      setSaving(false);
    }
  }

  function discard() {
    const base = getBaseline();
    if (!base) return;
    setDesign(base);
    setError(null);
    setMessage(null);
    markCleanAfterDiscard(base);
  }

  return (
    <Page
      title={t("Edit free gift pop-up")}
      subtitle={t("Fully customizable · responsive desktop & mobile")}
      backAction={{
        content: t("Back"),
        onAction: () => navigate(returnTo),
      }}
      primaryAction={
        !embedded
          ? {
              content: t("Save pop-up"),
              loading: saving,
              disabled: !dirty,
              onAction: () => void save(),
            }
          : undefined
      }
      secondaryActions={[
        {
          content: t("Reset defaults"),
          onAction: () => setDesign({ ...DEFAULT_POPUP_DESIGN }),
        },
      ]}
    >
      <ContextualSaveBar
        id="freebi-popup-save"
        open={embedded && dirty && !loading}
        saving={saving}
        onSave={save}
        onDiscard={discard}
        saveLabel="Save"
      />
      <SavingOverlay open={saving} label={t("Saving pop-up…")} />
      <BlockStack gap="400">
        {message && (
          <Banner tone="success" onDismiss={() => setMessage(null)}>
            {message}
          </Banner>
        )}
        {error && (
          <Banner tone="critical" onDismiss={() => setError(null)}>
            {error}
          </Banner>
        )}
        {loading && (
          <Banner tone="info">
            <p>{t("Loading saved pop-up…")}</p>
          </Banner>
        )}

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'minmax(280px, 380px) minmax(0, 1fr)',
            gap: 16,
            alignItems: 'start',
          }}
        >
          <Card>
            <BlockStack gap="400">
              <Text as="h2" variant="headingMd">
                {t("Default content")}
              </Text>
              <Text as="p" tone="subdued" variant="bodySm">
                {t("Fallback title & description when a campaign doesn’t set its own.\n                Each campaign can override these under Display settings.")}
              </Text>
              <FormLayout>
                <TextField
                  label={t("Default title")}
                  value={design.title}
                  onChange={(v) => patch('title', v)}
                  autoComplete="off"
                />
                <TextField
                  label={t("Default description")}
                  value={design.description}
                  onChange={(v) => patch('description', v)}
                  multiline={3}
                  autoComplete="off"
                />
                <TextField
                  label={t("Primary button")}
                  value={design.ctaLabel}
                  onChange={(v) => patch('ctaLabel', v)}
                  autoComplete="off"
                />
                <TextField
                  label={t("Secondary link")}
                  value={design.secondaryLabel}
                  onChange={(v) => patch('secondaryLabel', v)}
                  autoComplete="off"
                />
                <Checkbox
                  label={t("Show close button")}
                  checked={design.showClose}
                  onChange={(v) => patch('showClose', v)}
                />
              </FormLayout>

              <Text as="h2" variant="headingMd">
                {t("Auto-fit with your theme")}
              </Text>
              <Banner tone="info">
                <p>
                  {t("Sync the widget colors with your store’s active theme\n                  automatically. When enabled, the storefront samples button and\n                  text colors from the live theme so the pop-up matches your\n                  brand without manual pickers.")}
                </p>
              </Banner>
              <Checkbox
                label={t("Auto-fit with your theme")}
                checked={design.autoFitTheme}
                onChange={(v) => patch('autoFitTheme', v)}
                helpText={t("Uses your theme’s primary button / text colors on the storefront")}
              />
              <ThemeColorRefresh
                onApply={(c) => {
                  setDesign((d) => ({
                    ...d,
                    autoFitTheme: true,
                    themeSchemeId: c.schemeId || d.themeSchemeId,
                    accentColor: c.accentColor || d.accentColor,
                    backgroundColor: c.backgroundColor || d.backgroundColor,
                    textColor: c.textColor || d.textColor,
                    mutedColor: c.mutedColor || d.mutedColor,
                    buttonTextColor: c.buttonTextColor || d.buttonTextColor,
                    selectedBorder:
                      c.selectedBorder || c.accentColor || d.selectedBorder,
                    giftCardBg: c.giftCardBg || d.giftCardBg,
                    giftCardBorder: c.giftCardBorder || d.giftCardBorder,
                    overlayColor: c.overlayColor || d.overlayColor,
                  }));
                }}
              />

              <Text as="h2" variant="headingMd">
                {t("Style")}
              </Text>
              {design.autoFitTheme && (
                <Banner tone="warning">
                  <p>
                    {t("Manual colors are saved as fallbacks but won’t apply while\n                    Auto-fit is on.")}
                  </p>
                </Banner>
              )}
              <FormLayout>
                <ColorField
                  label={t("Accent")}
                  value={design.accentColor}
                  onChange={(v) => {
                    patch('accentColor', v);
                    patch('selectedBorder', v);
                  }}
                  disabled={design.autoFitTheme}
                />
                <ColorField
                  label={t("Background")}
                  value={design.backgroundColor}
                  onChange={(v) => patch('backgroundColor', v)}
                  disabled={design.autoFitTheme}
                />
                <ColorField
                  label={t("Text color")}
                  value={design.textColor}
                  onChange={(v) => patch('textColor', v)}
                  disabled={design.autoFitTheme}
                />
                <ColorField
                  label={t("Muted text")}
                  value={design.mutedColor}
                  onChange={(v) => patch('mutedColor', v)}
                  disabled={design.autoFitTheme}
                />
                <ColorField
                  label={t("Button text color")}
                  value={design.buttonTextColor}
                  onChange={(v) => patch('buttonTextColor', v)}
                  disabled={design.autoFitTheme}
                />
                <ColorField
                  label={t("Gift card background")}
                  value={design.giftCardBg}
                  onChange={(v) => patch('giftCardBg', v)}
                  disabled={design.autoFitTheme}
                />
                <ColorField
                  label={t("Gift card border")}
                  value={design.giftCardBorder}
                  onChange={(v) => patch('giftCardBorder', v)}
                  disabled={design.autoFitTheme}
                />
                <ColorField
                  label={t("Selected gift border")}
                  value={design.selectedBorder}
                  onChange={(v) => patch('selectedBorder', v)}
                  disabled={design.autoFitTheme}
                />
                <TextField
                  label={t("Overlay (desktop)")}
                  value={design.overlayColor}
                  onChange={(v) => patch('overlayColor', v)}
                  autoComplete="off"
                  disabled={design.autoFitTheme}
                  helpText={t("CSS color, e.g. rgba(0,0,0,0.5)")}
                />
                <RangeSlider
                  label={`Corner radius (${design.borderRadius}px)`}
                  value={design.borderRadius}
                  min={0}
                  max={28}
                  onChange={(v) =>
                    patch('borderRadius', Array.isArray(v) ? v[0] : v)
                  }
                  output
                />
                <RangeSlider
                  label={`Desktop max width (${design.maxWidth}px)`}
                  value={design.maxWidth}
                  min={320}
                  max={560}
                  onChange={(v) =>
                    patch('maxWidth', Array.isArray(v) ? v[0] : v)
                  }
                  output
                />
              </FormLayout>

              {!embedded && (
                <Button
                  variant="primary"
                  loading={saving}
                  disabled={!dirty}
                  onClick={() => void save()}
                >
                  {t("Save pop-up")}
                </Button>
              )}
            </BlockStack>
          </Card>

          <Card>
            <BlockStack gap="300">
              <InlineStack align="space-between" blockAlign="center">
                <Text as="h2" variant="headingMd">
                  {t("Live preview")}
                </Text>
                <ButtonGroup variant="segmented">
                  <Button
                    pressed={previewMode === 'desktop'}
                    onClick={() => setPreviewMode('desktop')}
                  >
                    {t("Desktop")}
                  </Button>
                  <Button
                    pressed={previewMode === 'mobile'}
                    onClick={() => setPreviewMode('mobile')}
                  >
                    {t("Mobile")}
                  </Button>
                </ButtonGroup>
              </InlineStack>
              <Text as="p" tone="subdued" variant="bodySm">
                {t("Desktop: centered modal · Mobile: bottom sheet — same design tokens.")}
              </Text>
              <div style={{ maxWidth: previewMode === 'mobile' ? 360 : '100%', margin: '0 auto' }}>
                <PopupPreview design={design} mode={previewMode} selectedCount={1} />
              </div>
            </BlockStack>
          </Card>
        </div>
      </BlockStack>
    </Page>
  );
}
